/**
 * Microcourse Studio — Excalidraw 白板渲染组件
 * 懒加载 @excalidraw/excalidraw，避免影响首屏体积
 */

import React, { Suspense, useMemo, useRef, useCallback } from 'react'
import type { ExcalidrawElement } from '@ai-tutor/shared'

// 懒加载 Excalidraw（~500KB gzip，仅在实际使用时加载）
const ExcalidrawCore = React.lazy(() =>
  import('@excalidraw/excalidraw').then((mod) => ({ default: mod.Excalidraw }))
)

// ─── Types ─────────────────────────────────────────────────────────

export interface ExcalidrawRendererProps {
  element: ExcalidrawElement
  /** 外部控制：是否处于编辑模式（覆盖 element.viewMode） */
  editable?: boolean
  /** 场景数据变更回调（编辑模式下） */
  onSceneChange?: (sceneJson: string) => void
}

interface ExcalidrawSceneData {
  elements?: unknown[]
  appState?: Record<string, unknown>
  files?: Record<string, unknown>
}

// ─── Helpers ───────────────────────────────────────────────────────

function parseScene(content: string): ExcalidrawSceneData | null {
  try {
    const parsed = JSON.parse(content)
    if (parsed && Array.isArray(parsed.elements)) {
      return parsed as ExcalidrawSceneData
    }
    return null
  } catch {
    return null
  }
}

// ─── Component ─────────────────────────────────────────────────────

export function ExcalidrawRenderer({ element, editable, onSceneChange }: ExcalidrawRendererProps) {
  const apiRef = useRef<any>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scene = useMemo(() => parseScene(element.content), [element.content])
  const isViewMode = editable ? false : (element.viewMode ?? true)

  // 诊断日志（定位“无法编辑”问题后可移除）
  console.log('[ExcalidrawRenderer]', { editable, viewMode: element.viewMode, isViewMode })

  const handleChange = useCallback(
    (elements: readonly unknown[]) => {
      if (!onSceneChange) return
      // 防抖 800ms 后回传
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const api = apiRef.current
        if (!api) return
        const appState = api.getAppState?.() ?? {}
        const files = api.getFiles?.() ?? {}
        const sceneJson = JSON.stringify({
          type: 'excalidraw',
          version: 2,
          elements: elements.filter((el: any) => !el.isDeleted),
          appState: { viewBackgroundColor: appState.viewBackgroundColor ?? '#ffffff' },
          files,
        })
        onSceneChange(sceneJson)
      }, 800)
    },
    [onSceneChange]
  )

  // 解析失败时显示降级内容
  if (!scene) {
    return (
      <div
        style={{ height: element.height ?? 360 }}
        className="flex items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400"
      >
        {element.title ?? '白板数据无效'}
      </div>
    )
  }

  const initialData = {
    elements: (scene.elements ?? []) as any,
    appState: {
      viewBackgroundColor: (scene.appState as any)?.viewBackgroundColor ?? '#ffffff',
      ...scene.appState,
    },
    files: scene.files ?? {},
    scrollToContent: true,
  }

  return (
    <div
      className="excalidraw-element-wrapper overflow-hidden rounded-lg border border-slate-200"
      style={{ height: element.height ?? 420 }}
    >
      {element.title && (
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500">
          <span>{element.title}</span>
          <span className={isViewMode ? 'text-slate-400' : 'text-green-600'}>
            {isViewMode ? '🔒 只读' : '✏️ 可编辑'}
          </span>
        </div>
      )}
      <div style={{ height: element.title ? `calc(${element.height ?? 420}px - 33px)` : '100%' }}>
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center bg-slate-50">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            </div>
          }
        >
          <ExcalidrawCore
            excalidrawAPI={(api: any) => { apiRef.current = api }}
            initialData={initialData as any}
            viewModeEnabled={isViewMode}
            zenModeEnabled={false}
            gridModeEnabled={false}
            theme="light"
            onChange={isViewMode ? undefined : handleChange}
            UIOptions={{
              canvasActions: {
                toggleTheme: false,
                changeViewBackgroundColor: !isViewMode,
                export: { saveFileToDisk: false },
                loadScene: false,
                saveToActiveFile: false,
              },
            }}
          />
        </Suspense>
      </div>
    </div>
  )
}
